<template>
  <div class="d-flex flex-grow-1">
    <div v-if="editing">
      <div v-for="(fieldset, fieldsetIndex) in getConfig.fieldsets" :key="fieldsetIndex">
        <h4 v-if="isXForm">{{ xformLabels[fieldsetIndex] }}</h4>
        <div class="vector-container d-flex">
          <div v-for="(field, index) in fieldset" :key="index" class="form-group">
            <label>{{ $t(field) }}</label>
            <input
                :id="field"
                :ref="field"
                :value="isXForm ? getXFormValue(fieldsetIndex, index) : getVectorValue(index)"
                @input="isXForm ? setXFormValue(fieldsetIndex, index, $event.target.value) : setVectorValue(index, $event.target.value)"
                class="form-text form-text-dark"
                type="text"
                inputmode="decimal"
                @keydown.enter="save"
            >
          </div>
        </div>
      </div>
      <div
          v-if="!hideSaveButton"
          class="mt-3 settings-button d-inline-block"
          @click="save"
      >
        <icon type="save"/>
        {{ $t('SAVE') }}
      </div>
    </div>
    <div v-else>
      <vector-display
          :type="type"
          :value="value"
      />
    </div>
  </div>
</template>

<script>
import {VectorConfigs} from '@/enum';
import {MetaFieldTypes} from '@/enum';
import VectorDisplay from "./VectorDisplay";
import Icon from "../../Icon";

/**
 * Widget for editing Vector Data
 * @displayName Vector Widget
 */
export default {
  name: "VectorWidget",
  components: {
    Icon,
    VectorDisplay
  },
  props: {
    /**
     * A Json or Array for coordinates – must fit the corresponding type
     * @values (e.g. [0,0] for Vector 2, [[0,0,0,0], [0,0,0,0], [0,0,0,0]] for xForm3 (loc, rot, scale)
     */
    value: {type: () => {return Array || String}, default: null},
    /**
     * The type of the vector-data
     * @values Vector2, Vector3, Vector4, xForm2, xForm3
     */
    type: {type: String, default: 'xform2'},
    hideSaveButton: {type: Boolean, default: false},
    editing: {type: Boolean, default: false}
  },
  data() {
    return {
      xformLabels: ['position', 'rotation', 'scale'],
      values: [],
      localValues: []
    };
  },
  computed: {
    getConfig() {
      return VectorConfigs[this.type];
    },
    isXForm() {
      return this.type === MetaFieldTypes.xForm2 || this.type === MetaFieldTypes.xForm3;
    },
  },
  watch: {
    values() {
      this.$emit('update', this.values);
    }
  },
  beforeMount() {
    if (this.value && this.value.length > 0 && this.value !== '[]') {
      let arr = this.value;
      if (arr.length > 0 && !Array.isArray(this.value)) {
        arr = JSON.parse(this.value)
      }
      this.values = arr;
      this.localValues = this.isXForm
        ? arr.map(row => row.map(v => String(v)))
        : arr.map(v => String(v));
    } else {
      //generate an empty array with the necessary fields
     if(!this.isXForm) {
       this.initEmptyVector();
     } else {
       this.initEmptyXForm();
     }
    }
  },
  methods: {
    initEmptyVector() {
      let fieldsets = VectorConfigs[this.type].fieldsets;
      let arr = [];
      let localArr = [];
      for (let i = 0; i < fieldsets.length; i++) {
        for (let j = 0; j < fieldsets[i].length; j++) {
          arr.push(0);
          localArr.push('0');
        }
      }
      this.values = arr;
      this.localValues = localArr;
    },
    initEmptyXForm() {
      let fieldsets = VectorConfigs[this.type].fieldsets;
      let arr = [];
      let localArr = [];
      for (let i = 0; i < fieldsets.length; i++) {
        let tmpArr = [];
        let localTmpArr = [];
        for (let j = 0; j < fieldsets[i].length; j++) {
          tmpArr.push(0);
          localTmpArr.push('0');
        }
        arr.push(tmpArr);
        localArr.push(localTmpArr);
      }
      this.values = arr;
      this.localValues = localArr;
    },
    setVectorValue(index, val) {
      this.$set(this.localValues, index, val);
      const parsed = parseFloat(val);
      this.$set(this.values, index, isNaN(parsed) ? 0 : parsed);
    },
    setXFormValue(fieldsetIndex, index, val) {
      if (!this.localValues[fieldsetIndex]) {
        this.$set(this.localValues, fieldsetIndex, []);
      }
      this.$set(this.localValues[fieldsetIndex], index, val);
      const parsed = parseFloat(val);
      if (!isNaN(parsed)) {
        this.$set(this.values[fieldsetIndex], index, parsed);
      }
    },
    getVectorValue(index) {
      if (index < this.localValues.length) {
        return this.localValues[index];
      }
      if (this.values.length <= index) {
        return 0;
      }
      return this.values[index] !== undefined ? this.values[index] : 0;
    },
    getXFormValue(fieldsetIndex, index) {
      if (this.localValues[fieldsetIndex] && this.localValues[fieldsetIndex][index] !== undefined) {
        return this.localValues[fieldsetIndex][index];
      }
      if (this.values.length <= fieldsetIndex) {
        return 0;
      }
      if (this.values[fieldsetIndex].length <= index) {
        return 0;
      }
      return this.values[fieldsetIndex][index] !== undefined ? this.values[fieldsetIndex][index] : 0;
    },
    save() {
      /**
       * save event
       * @param {array}
       */
      this.$emit('save', this.values);
    },
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  position: relative;
}

label {
  margin-bottom: 0;
}

.fieldset {
  background-color: $panel-title-background-color;
  padding: 8px;
}

.label-left {
  float: left;
  margin: 0;
  padding-top: ceil($text-input-padding / 2);
  margin-right: 15px;
}

.vector-container {
  .form-group {
    width: 32%;
    margin-right: 0.5%;
    display: inline-block;
    margin-bottom: 0;
    min-width: 60px;

    input {
      width: 100%;
    }
  }
}
</style>